"use client";

import Image from "next/image";
import { useState, useRef, useEffect } from "react";
import { FloatButton, Modal, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCommentDots,
  faPhone,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useIsDesktop } from "@/hooks/app/useIsDesktop";
import { useEdgeDraggable } from "@/hooks/app/useEdgeDraggable";
import BackTopIcon from "@/components/icons/BackTopIcon";
import PhoneIcon from "@/components/icons/PhoneIcon";

declare global {
  interface Window {
    oscWidget?: {
      init: (config: { token: string }) => void;
      open?: () => void;
      show?: () => void;
      isOpen?: () => boolean;
      _inited?: boolean;
      isOpen_val?: boolean;
    };
  }
}

const OMI_WIDGET_SRC = process.env.NEXT_PUBLIC_OMI_WIDGET_SRC || "";
const OMI_WIDGET_TOKEN = process.env.NEXT_PUBLIC_OMI_WIDGET_TOKEN || "";
const OMI_OPEN_BODY_CLASS = "omi-chat-open";

const GlobalActions = () => {
  const [expanded, setExpanded] = useState(false);
  const [modal, modalCtx] = Modal.useModal();
  const isDesktop = useIsDesktop();
  const { t } = useTranslation();
  const phone = process.env.NEXT_PUBLIC_COMPANY_PHONE || "";
  const omiLoadedRef = useRef(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const groupRef = useRef<HTMLDivElement>(null);

  const { position, isDragging, wasDraggedRef, dragHandlers } =
    useEdgeDraggable(groupRef);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      document.body.classList.remove(OMI_OPEN_BODY_CLASS);
    };
  }, []);

  const callUs = () => {
    if (isDesktop) {
      window.location.href = `tel:${phone}`;
      return;
    }

    modal.confirm({
      icon: <PhoneIcon className="hotline__icon" />,
      title: `${t("app.CSKH")}: ${phone}`,
      content: t("app.hotline_message", { timeline: "07:00 - 23:00" }),
      okText: t("app.call_now"),
      onOk: () => {
        window.location.href = `tel:${phone}`;
      },
    });
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src = OMI_WIDGET_SRC;
    script.async = true;
    script.onload = () => {
      window.oscWidget?.init({ token: OMI_WIDGET_TOKEN });
      omiLoadedRef.current = true;
    };
    document.body.appendChild(script);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      document.body.classList.remove(OMI_OPEN_BODY_CLASS);
    };
  }, []);

  const watchOmiOpenState = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      const isOpen = window.oscWidget?.isOpen?.();
      if (isOpen === false) {
        document.body.classList.remove(OMI_OPEN_BODY_CLASS);
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    }, 100);
  };

  const openOmiChat = () => {
    const btn = document.querySelector<HTMLElement>("#osc-button");

    if (window.oscWidget?._inited && btn) {
      btn.click();
      document.body.classList.add(OMI_OPEN_BODY_CLASS);
      watchOmiOpenState();
      return;
    }

    console.warn("[OMI] widget not initialized yet");
  };

  const scrollToTop = () => {
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
  };

  const handleChatClick = () => {
    if (wasDraggedRef.current) {
      wasDraggedRef.current = false;
      return;
    }
    setExpanded((value) => !value);
  };

  return (
    <>
      {modalCtx}
      <div
        ref={groupRef}
        className={`global-actions ${expanded ? "is-expanded" : ""} ${
          position?.side === "left" ? "is-left" : ""
        } ${isDragging ? "is-dragging" : ""}`}
        style={
          position ? { top: `${position.top}px`, bottom: "auto" } : undefined
        }
      >
        <Tooltip
          title={t("app.chat_tooltip_title")}
          placement="left"
          style={{ pointerEvents: "none" }}
          mouseLeaveDelay={0}
        >
          <FloatButton
            type="primary"
            className="global-actions__contact global-actions__contact--omi"
            icon={<FontAwesomeIcon icon={faCommentDots} />}
            onClick={openOmiChat}
          />
        </Tooltip>
        <Tooltip
          title={t("app.chat_by_zalo")}
          placement="left"
          style={{ pointerEvents: "none" }}
          mouseLeaveDelay={0}
        >
          <FloatButton
            type="primary"
            className="global-actions__contact global-actions__contact--zalo"
            icon={
              <Image
                className="global-actions__zalo-icon"
                alt="Zalo"
                src="/static/zalo-icon.png"
                width={24}
                height={24}
              />
            }
            onClick={() =>
              window.open("https://zalo.me/1077575192741117044", "_blank")
            }
          />
        </Tooltip>
        <Tooltip
          title={`${t("app.call_us")}: ${phone}`}
          placement="left"
          style={{ pointerEvents: "none" }}
          mouseLeaveDelay={0}
        >
          <FloatButton
            type="primary"
            className="global-actions__contact global-actions__contact--phone"
            icon={<FontAwesomeIcon icon={faPhone} />}
            onClick={callUs}
          />
        </Tooltip>
        {/* Plain FloatButton instead of FloatButton.BackTop: BackTop's CSSMotion
            renders nothing on the client's first render but a button on the
            server, which causes a hydration mismatch. */}
        <FloatButton
          type="primary"
          className="global-actions__top"
          icon={<BackTopIcon />}
          onClick={scrollToTop}
          onPointerDown={dragHandlers.onPointerDown}
          onPointerMove={dragHandlers.onPointerMove}
          onPointerUp={dragHandlers.onPointerUp}
        />

        <div className="global-actions__chat-wrap">
          <FloatButton
            type={expanded ? "default" : "primary"}
            className="global-actions__chat"
            icon={
              <>
                <FontAwesomeIcon icon={expanded ? faXmark : faCommentDots} />
                {!expanded && (
                  <span
                    className="global-actions__chat-rays"
                    aria-hidden="true"
                  >
                    <i />
                    <i />
                    <i />
                  </span>
                )}
              </>
            }
            onClick={handleChatClick}
            onPointerDown={dragHandlers.onPointerDown}
            onPointerMove={dragHandlers.onPointerMove}
            onPointerUp={dragHandlers.onPointerUp}
            aria-label={
              expanded
                ? t("app.close_contact_actions")
                : t("app.open_contact_actions")
            }
            style={{ fontSize: "2rem", touchAction: "none" }}
          />
          {!expanded && (
            <div
              className="global-actions__chat-tooltip-content"
              role="tooltip"
            >
              <strong>{t("app.chat_tooltip_title")}</strong>
              <span>{t("app.chat_tooltip_description")}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default GlobalActions;
